import { useCallback, useLayoutEffect, useRef, useState } from 'react';

export interface UseInViewOnceOptions {
  root?: Element | null;
  /** Defaults to roughly one viewport ahead/behind, so content is ready before it is scrolled to. */
  rootMargin?: string;
  threshold?: number | number[];
}

export interface UseInViewOnceResult<T extends Element = Element> {
  ref: (node: T | null) => void;
  inView: boolean;
}

const DEFAULT_ROOT_MARGIN = '100% 0px 100% 0px';

/**
 * The first screen of a gallery must paint without waiting for a scroll. A node that is already
 * inside the viewport — or that has not been measured yet (0×0, which is what jsdom and a
 * not-yet-laid-out box report) — is treated as in view immediately. Only a box that has a real
 * size and sits entirely outside the viewport stays deferred until `IntersectionObserver` fires.
 * A silent observer (present, but never calling back) must not leave a visible card blank.
 */
function isOnInitialViewport(node: Element): boolean {
  const rect = node.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return true;

  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  const horizontally = rect.right > 0 && rect.left < viewportWidth;
  const vertically = rect.bottom > 0 && rect.top < viewportHeight;
  return horizontally && vertically;
}

/**
 * Reports whether the referenced element has EVER intersected the viewport (or a custom
 * `root`), then disconnects — it never flips back to `false`. Unlike `useIsInViewport`
 * (`@/common/presentation/perf/useIsInViewport`, which tracks intersection continuously
 * for viewport-aware effects), this hook is for one-shot lazy mounts: a library card
 * preview should stay rendered once it has been seen, even after the user scrolls it
 * back out of view, rather than un-mounting and re-mounting its (expensive) live render
 * on every scroll pass.
 *
 * SSR/older-browser-safe: when `IntersectionObserver` is unavailable, `inView` resolves
 * to `true` immediately on mount so content is never permanently hidden behind a missing
 * API.
 */
export function useInViewOnce<T extends Element = Element>(options: UseInViewOnceOptions = {}): UseInViewOnceResult<T> {
  const { root = null, rootMargin = DEFAULT_ROOT_MARGIN, threshold = 0 } = options;
  const [node, setNode] = useState<T | null>(null);
  const [inView, setInView] = useState(false);
  const hasFiredRef = useRef(false);

  const ref = useCallback((element: T | null) => {
    setNode(element);
  }, []);

  useLayoutEffect(() => {
    if (hasFiredRef.current) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      // No observer API at all (SSR / unsupported browser): resolve immediately regardless of
      // whether a node has been attached yet, rather than waiting forever on an API that will
      // never fire.
      hasFiredRef.current = true;
      setInView(true);
      return undefined;
    }

    if (!node) return undefined;

    if (isOnInitialViewport(node)) {
      hasFiredRef.current = true;
      setInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry?.isIntersecting) {
          hasFiredRef.current = true;
          setInView(true);
          observer.disconnect();
        }
      },
      { root, rootMargin, threshold }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [node, root, rootMargin, threshold]);

  return { ref, inView };
}
